import _jsx from "@babel/runtime/helpers/jsx";
var _Glyphicon, _span;
import React, { memo, useEffect, useState } from 'react';
import { Cookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import { useFeatures } from '@atc/features';
import { Button, Glyphicon, IconButton } from 'reaxl';
import { injectScript } from '@atc/dom-fns';
import { getFirstPartyVistorId, getPixallId } from '@atc/pixall-fns';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
const noop = () => {};

/**
 * Renders the button for chat.
 * @param {string} className        - classname to add to button
 * @param {function} onChatClick    - callback for clicking the chat button
 * @param {object} props            - properties to pass to button
 * @param {string} uiContext        - uiContext to render different styles of button
 */
export const renderButton = (className, onChatClick, props, uiContext, isDealerNameRelocation) => {
  if (uiContext === 'iconButton') {
    return /*#__PURE__*/_jsx(IconButton, {
      label: isDealerNameRelocation ? 'Chat' : 'Chat with Us',
      glyph: "commenting-o",
      onClick: onChatClick
    });
  }
  if (uiContext === 'iconButtonStacked') {
    return /*#__PURE__*/_jsx(IconButton, {
      glyph: "commenting-o",
      label: "Chat",
      layout: "stacked",
      onClick: onChatClick,
      size: "large"
    });
  }
  const bsStyle = {
    button: 'secondary',
    link: 'link'
  }[uiContext];
  return /*#__PURE__*/React.createElement(Button, Object.assign({}, props, {
    className: className,
    block: uiContext === 'button',
    bsStyle: bsStyle,
    onClick: onChatClick
  }), _Glyphicon || (_Glyphicon = /*#__PURE__*/_jsx(Glyphicon, {
    glyph: "commenting-o",
    className: "margin-right-2",
    "aria-hidden": "true"
  })), "Chat with Dealer");
};
const prepareChatData = (listing, user) => {
  let chatData;
  if (listing) {
    chatData = {
      vehicleStatus: listing.listingType,
      vehicleMake: listing.make,
      vehicleModel: listing.model,
      vehicleYear: listing.year,
      vehicleVin: listing.vin,
      vehicleId: listing.id
    };
  }
  const {
    location = {}
  } = user;
  const {
    zip: zipcode
  } = location;
  const cookies = new Cookies();
  const consumerIP = cookies.get('pxa_ipv4');
  const pixallFirstPartyId = getFirstPartyVistorId();
  const pixallThirdPartyId = getPixallId() || 'nl';
  const visitData = {
    zipcode,
    pixallFirstPartyId,
    pixallThirdPartyId,
    IP: consumerIP
  };
  return {
    chatData,
    visitData
  };
};
const populateChatAttributes = (chatEnv, merchantId, hideStickyInvitation, aeAccountUrl, listing, user) => {
  const {
    chatData,
    visitData
  } = prepareChatData(listing, user);
  const bodyElement = document.getElementsByTagName('body')[0];
  bodyElement.setAttribute('chat-account', `${merchantId}.${aeAccountUrl}`);
  bodyElement.setAttribute('chat-env', chatEnv);
  if (hideStickyInvitation) {
    bodyElement.setAttribute('chat-config-label', 'behindbutton');
  }
  if (chatData) {
    const chatDataString = JSON.stringify(chatData);
    bodyElement.setAttribute('chat-data', chatDataString);
  }
  if (!_isEmpty(visitData)) {
    const chatVisitDataString = JSON.stringify(visitData);
    bodyElement.setAttribute('chat-visitdata', chatVisitDataString);
  }
};
const cleanupChatAttributes = () => {
  const bodyElement = document.getElementsByTagName('body')[0];
  ['chat-account', 'chat-env', 'chat-data', 'chat-visitdata', 'chat-config-label'].forEach(attr => bodyElement.hasAttribute(attr) && bodyElement.removeAttribute(attr));
};
const OwnerChatActivEngage = /*#__PURE__*/memo(_ref => {
  let {
    className,
    listing,
    onChatClick,
    merchantId,
    uiContext,
    showChatCTA,
    ...rest
  } = _ref;
  const [hasScript, setHasScript] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(!window.ActivEngage);
  const user = useSelector(state => state.user || {}, _isEqual);
  const activEngageUrl = useSelector(state => _get(state, 'configs.activEngageUrl', ''), _isEqual);
  const {
    is_production: [isProduction],
    chat: [, {
      hide_sticky_invitation: hideStickyInvitation,
      ae_account_url: aeAccountUrl
    }],
    dealer_name_relocation: [isDealerNameRelocationFlagEnabled]
  } = useFeatures(['is_production', 'chat', 'dealer_name_relocation']);
  const chatEnv = isProduction ? 'production' : 'qa';
  const isPrivateSeller = _get(listing, 'owner.privateSeller', false);
  const isDealerNameRelocation = isDealerNameRelocationFlagEnabled && !isPrivateSeller;
  window.AE = {};
  window.AE.onAvailable = noop;
  window.AE.onUnavailable = noop;
  window.AE.onLaunch = noop;

  // Handle direct hit or first SPA transition
  // Sets up data and injects script
  useEffect(() => {
    if (!window.ActivEngage) {
      populateChatAttributes(chatEnv, merchantId, hideStickyInvitation, aeAccountUrl, listing, user);
      injectScript(activEngageUrl).then(() => {
        setHasScript(true);
      }).catch(error => {
        if (window && window.newrelic) {
          window.newrelic.addPageAction('ThirdPartyError', {
            thirdPartyName: 'ActivEngage',
            component: 'OwnerChatActivEngage',
            message: 'Error injecting ActivEngage script',
            status: error?.status || 'unknown',
            url: window.location.href,
            error
          });
        }
      });
    } else {
      setHasScript(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handles all SPA transitions after first
  useEffect(() => {
    if (hasScript) {
      if (!isFirstLoad) {
        populateChatAttributes(chatEnv, merchantId, hideStickyInvitation, aeAccountUrl, listing, user);
        window.ActivEngage?.spacontrol?.run();
      } else {
        setIsFirstLoad(false);
      }
      // Always rebind on every load to ensure the chat button works correctly
      window.ActivEngage?.rebind?.();
      return () => {
        cleanupChatAttributes();
        window.ActivEngage?.spacontrol?.stop();
      };
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScript, listing.id]);
  if (!hasScript) {
    return null;
  }

  /* eslint-disable react/no-unknown-property */
  return /*#__PURE__*/_jsx("div", {
    id: "owner-chat-container-activengage",
    "chat-launch": "plainbutton",
    "chat-onavailable": "AE.onAvailable",
    "chat-onunavailable": "AE.onUnavailable",
    "chat-onlaunch": "AE.onLaunch",
    className: "display-inline display-sm-block"
  }, `owner-chat-${listing.id}`, showChatCTA ? renderButton(className, onChatClick, rest, uiContext, isDealerNameRelocation) : _span || (_span = /*#__PURE__*/_jsx("span", {})));
  /* eslint-enable react/no-unknown-property */
}, (prev, next) => prev.listing?.id === next.listing?.id);
export default OwnerChatActivEngage;